import { Button, Form, FormInstance, Input, InputRef, message, Popconfirm, Space } from 'antd';
import { useContext, useEffect, useRef, useState } from 'react'
import SidebarItem from './sidebar-item';
import './sidebar.less'
import {
    PlusOutlined,
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom';
import { NavigationContext } from '@/App';
import classnames from 'classnames';
import { Project } from '@/declare/interface/project/project';
import { NavigationStatus } from '@/store/navigation-context';
import { userCheckLogin } from '@/declare/api/project/user';
import { User } from '@/declare/interface/project/user';
import { ProjectGroup } from '@/declare/interface/project/project-group';
import { createProjectGroup, deleteProjectGroup, findProjectGroups, updateProjectGroup } from '@/declare/api/project/project-group';
import { ContextMenu, Modal } from 'bimui';
/**
 * 接口 IsidebarItem
 * 
 * @property {any} icon 项目的图标
 * @property {boolean} expanded 项目是否展开
 * @property {IsidebarItem[]} children 子项目
 * @property {string} url 项目的跳转链接
 * @property {number} key 项目的唯一标识符
 * @property {string} themeColor 项目的主题颜色
 * @property {boolean} hideArrow 是否隐藏箭头
 * @property {number} count 项目的计数值
 * @property {boolean} active 是否激活
 */
export interface IsidebarItem extends Project {
    icon?: any,
    expanded?: boolean,
    children?: IsidebarItem[],
    url?: string,
    key?: number,
    themeColor?: string,
    hideArrow?: boolean
    count?: number,
    active?: boolean,
}
/**
 * `GlobalSidebar` 组件
 * 
 * 显示项目列表的侧边栏
 * 实现创建、重命名、删除项目
 */

interface Iprops {
    onProjectCreate?: (value: number) => void
}
export default function GlobalSidebar(props: Iprops) {
    /**
     * 保存项目列表的路由数据
     */
    const [routes, setRoutes] = useState<IsidebarItem[]>([]);
    /**
     * 控制新建项目模态框的显示状态
     */
    const [addProjectVisible, setAddProjectVisible] = useState<boolean>(false);
    /**
     * 控制重命名项目模态框的显示状态
     */
    const [renameProjectVisible, setRenameProjectVisible] = useState<boolean>(false);
    /**
     * 控制新建项目按钮的加载状态
     */
    const [addLoading, setAddLoading] = useState<boolean>(false);
    /**
     * 控制重命名项目按钮的加载状态
     */
    const [renameLoading, setRenameLoading] = useState<boolean>(false);
    /**
     * 当前激活的项目uuid
     */
    let [activedRouteUuid, setActivedRouteUuid] = useState<string>();
    /**
     * 操作新建项目或重命名项目的输入框
     */
    const ref = useRef<InputRef>(null);
    /**
     * 用于跳转不同页面
     */
    const navigate = useNavigate();
    /**
     * 访问全局的导航状态
     */
    const navigationContext = useContext<NavigationStatus>(NavigationContext);

    /**
     * 控制右键菜单的显示状态
     */
    let [cmVisible, setCmVisible] = useState(false);
    /**
     * 右键菜单的水平位置
     */
    let [contextMenuLeft, setContextMenuLeft] = useState<number>();
    /**
     * 右键菜单的垂直位置
     */
    let [contextMenuTop, setContextMenuTop] = useState<number>();
    /**
     * 当前选中的项目
     */
    const [selectProject, setSelectProject] = useState<IsidebarItem>();
    /**
     * 当前用户是否为 Demo 用户
     */
    const [isDemoUser, setIsDemoUser] = useState<boolean>();
    /**
     * 操作新建项目组模态框的表单
     */
    const formRef = useRef<FormInstance>(null);
    /**
     * 操作重命名模态框的表单
     */
    const renameRef = useRef<FormInstance>(null);
    /**
     * 检查用户类型
     */
    useEffect(() => {
        userCheckLogin()
            .then(res => {
                let userDetail: User = res.data;
                setIsDemoUser(userDetail.type === 3);
            })
    }, [])
    /**
     * 初始化项目列表数据
     */
    useEffect(() => {
        generateData(true);
    }, [])
    /**
     * 从后端获取项目列表，生成侧边栏数据
     */
    const generateData = (shoudNavigate: boolean = false) => {
        let isDemo = false;
        userCheckLogin()
            .then(res => {
                let userDetail: User = res.data;
                isDemo = userDetail.type === 3;
                setIsDemoUser(isDemo);

                return findProjectGroups();
            })
            .then(res => {
                let ps: ProjectGroup[] = res.data || [];
                if (isDemo) {
                    ps = ps.filter(item => item.demo);
                }
                let routes = ps.map(item => {
                    return {
                        url: `/desktop/${item.uuid}`,
                        name: item.name,
                        uuid: item.uuid
                    }
                });
                setRoutes(routes);

                if (shoudNavigate && routes.length > 0) {
                    let currentProjectUuid = navigationContext.currentProjectUuid;
                    let route = routes[0];
                    if (currentProjectUuid != null) {
                        let r = routes.find(item => item.uuid === currentProjectUuid);
                        if (r != null) {
                            route = r;
                        }
                    }
                    navigate(route.url);
                } else if (shoudNavigate) {
                    navigate(`/desktop`);
                }
            }).catch(err => {
                console.log(err);
            })
    }
    /**
     * 聚焦输入框
     */
    const focusInput = () => {
        setTimeout(() => {
            ref.current?.focus({
                cursor: 'end'
            })
        }, 1)
    }
    /**
     * 创建新项目组
     */
    const onCreateProjectGroup = (values) => {
        let data = {
            name: values['name'],
            isDemo: isDemoUser,
        }
        setAddLoading(true);
        createProjectGroup(data).then(res => {
            setAddProjectVisible(false);
            navigationContext.setCurrentProjectUuid(res.data);
            generateData(true);
            navigate(`/desktop/${res.data}`)
            setActivedRouteUuid(res.data);
        }).finally(() => {
            setAddLoading(false);
        })
    }
    /**
     * 重命名项目组
     */
    const onRename = (values) => {
        if (selectProject == null) {
            setRenameProjectVisible(false);
            return;
        }
        let data = {
            name: values['name']
        }
        setRenameLoading(true)
        updateProjectGroup(selectProject.uuid, data).then(res => {
            generateData();
            setRenameProjectVisible(false);
            setSelectProject(null)
        }).finally(() => {
            setRenameLoading(false)
        })
    }
    /**
     * 删除项目组
     */
    const onDelete = (value: IsidebarItem) => {
        if (value == null) {
            setCmVisible(false)
            return;
        }
        let projectUuid = value.uuid;
        deleteProjectGroup(projectUuid).then(res => {
            if (activedRouteUuid === projectUuid) {
                navigationContext.setCurrentProjectUuid(null);
                generateData(true);
            } else {
                generateData();
            }
        }).finally(() => {
            setCmVisible(false)
        })
    }

    return (
        <div className="sidebar-container">
            <div className="logo">
                <img src="/tn-graphics/logo-color.png" alt="tncet" height="40px" />
            </div>
            <div className='btn-group'>
                <Button type="primary" block onClick={e => {
                    setAddProjectVisible(true);
                    focusInput();
                }}>
                    新建项目组
                </Button>

            </div>
            <div className='scrollable'>
                {routes.map(item => (
                    <SidebarItem
                        route={item}
                        key={item.id}
                        activedRouteUuid={activedRouteUuid}
                        toggleActiveRoute={(id) => {
                            navigationContext.setCurrentProjectUuid(id);
                            setActivedRouteUuid(id)
                        }}
                        onContextMenu={e => {
                            e.preventDefault();
                            let clientX = e.clientX;
                            let clientY = e.clientY;
                            setContextMenuLeft(clientX);
                            setContextMenuTop(clientY);
                            setSelectProject(item)
                            setCmVisible(true);
                        }}
                        selected={selectProject?.id === item.id}
                    />
                ))}
            </div>

            <Modal
                open={addProjectVisible}
                title="添加项目组"
                onSave={() => {
                    formRef.current?.submit(); // 直接提交表单
                }}
                onCancel={() => setAddProjectVisible(false)}
                // onFocus={() => { inputRef.current && inputRef.current!.focus({ cursor: 'end', }) }}
                width={400}
                mask
            >
                <>
                    <Form
                        ref={formRef} // 绑定表单引用
                        size="small"
                        autoComplete="off"
                        onFinish={onCreateProjectGroup}
                        className="add-project-modal"
                        style={{ padding: '12px 12px 12px 12px' }}
                    >
                        <Form.Item
                            name="name"
                            rules={[
                                { required: true, message: '名称不能为空' },
                                { max: 200 },
                            ]}
                        >
                            <Input placeholder="请输入项目组名称" ref={ref} />
                        </Form.Item>
                    </Form>
                </>
            </Modal>
            <Modal
                open={renameProjectVisible}
                title="重命名"
                onSave={() => {
                    renameRef.current?.submit(); // 提交表单
                }}
                onCancel={() => {
                    setRenameProjectVisible(false);
                    setSelectProject(null);
                }}
                width={400}
                mask
            >
                <Form
                    ref={renameRef} // 绑定表单引用
                    size="small"
                    autoComplete="off"
                    onFinish={onRename} // 绑定重命名逻辑
                    className="rename-project-modal"
                    style={{ padding: '12px 12px 12px 12px' }}
                >
                    <Form.Item
                        name="name"
                        initialValue={selectProject?.name} // 设置初始值为当前项目名称
                        rules={[
                            { required: true, message: '名称不能为空' },
                            { max: 200 },
                        ]}
                    >
                        <Input placeholder="请输入项目组名称" ref={ref} />
                    </Form.Item>
                </Form>
            </Modal>
            <ContextMenu
                visible={cmVisible}
                left={contextMenuLeft}
                top={contextMenuTop}
                width={140}
                onCancel={() => {
                    setCmVisible(false);
                    setSelectProject(null)
                }}
                wrapperClassname='cm-container'
            >
                <div className='cm-body'>
                    <div className={classnames({
                        'option': true,
                        'disabled': false
                    })} onClick={() => {
                        setCmVisible(false);
                        setRenameProjectVisible(true);
                        focusInput();
                    }}>重命名</div>
                    <Popconfirm
                        title="确认删除"
                        onCancel={() => {
                            setSelectProject(null);
                            setCmVisible(false);
                        }}
                        onConfirm={() => onDelete(selectProject)}>
                        <div className={classnames({
                            'option': true,
                            'disabled': false
                        })}>删除项目</div>
                    </Popconfirm>


                </div>
            </ContextMenu>
        </div>
    )
}